import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { departmentMasterAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import { Building2 } from 'lucide-react';
import { EmptyState, CardSkeleton } from './common/States';
import { StatCard } from './common/StatCard';
import { useVertical } from '../contexts/VerticalContext';

const DepartmentMasterScreen = () => {
  const navigate = useNavigate();
  const { term } = useVertical();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deptForm, setDeptForm] = useState({ name: '', location: '' });
  const [subForms, setSubForms] = useState({});
  const [designationForms, setDesignationForms] = useState({});

  const loadRows = async () => {
    try {
      setLoading(true);
      const res = await departmentMasterAPI.list();
      setRows(res.data || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load department master'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, []);

  const createDepartment = async (e) => {
    e.preventDefault();
    try {
      await departmentMasterAPI.createDepartment(deptForm);
      setDeptForm({ name: '', location: '' });
      toast.success('Department created');
      await loadRows();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create department'));
    }
  };

  const toggleDepartment = async (row) => {
    try {
      await departmentMasterAPI.updateDepartment(row.id, { is_active: !row.is_active });
      await loadRows();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update department status'));
    }
  };

  const addSubDepartment = async (departmentId) => {
    try {
      const name = (subForms[departmentId] || '').trim();
      if (!name) return;
      await departmentMasterAPI.createSubDepartment(departmentId, { name });
      setSubForms((prev) => ({ ...prev, [departmentId]: '' }));
      toast.success('Sub department added');
      await loadRows();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create sub department'));
    }
  };

  const bootstrapITCatalog = async () => {
    try {
      const res = await departmentMasterAPI.bootstrapITCatalog();
      const data = res.data || {};
      toast.success(
        `IT catalog synced: ${data.created_departments || 0} departments, ${data.created_sub_departments || 0} sub departments added`
      );
      await loadRows();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to sync IT catalog'));
    }
  };

  const toggleSubDepartment = async (sub) => {
    try {
      await departmentMasterAPI.updateSubDepartment(sub.id, { is_active: !sub.is_active });
      await loadRows();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update sub department status'));
    }
  };

  const addDesignation = async (departmentName, subDepartmentName, subDepartmentId) => {
    try {
      const key = String(subDepartmentId);
      const name = (designationForms[key] || '').trim();
      if (!name) return;
      await departmentMasterAPI.createDesignation({
        department_name: departmentName,
        sub_department_name: subDepartmentName,
        name,
      });
      setDesignationForms((prev) => ({ ...prev, [key]: '' }));
      toast.success('Designation added');
      await loadRows();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create designation'));
    }
  };

  const toggleDesignation = async (designation) => {
    try {
      await departmentMasterAPI.updateDesignation(designation.id, { is_active: !designation.is_active });
      await loadRows();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update designation status'));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-foreground">{term('department', 'Department')} Master</h2>
          <p className="text-sm text-muted-foreground">Manage {term('department', 'department').toLowerCase()}s, sub-{term('department', 'department').toLowerCase()}s and designations hierarchy.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/admin/master/departments')}>Departments CRUD</Button>
          <Button variant="outline" onClick={() => navigate('/admin/master/subdepartments')}>Sub-Departments CRUD</Button>
          <Button variant="outline" onClick={() => navigate('/admin/master/designations')}>Designations CRUD</Button>
          <Button variant="outline" onClick={bootstrapITCatalog}>Sync IT Catalog</Button>
          <Button variant="outline" onClick={() => navigate('/admin/import/department_master')}>Import Departments</Button>
          <Button variant="outline" onClick={() => navigate('/admin/import/sub_department_master')}>Import Sub Departments</Button>
        </div>
      </div>

      <StatCard label={`Total ${term('department', 'Department')}s`} value={rows.length} icon={Building2} tone="primary" />

      <Card>
        <CardHeader>
          <CardTitle>Add {term('department', 'Department')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="grid gap-3 sm:grid-cols-2 md:grid-cols-3" onSubmit={createDepartment}>
            <div>
              <Label>{term('department', 'Department')} Name</Label>
              <Input
                value={deptForm.name}
                onChange={(e) => setDeptForm({ ...deptForm, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Department Location</Label>
              <Input
                value={deptForm.location}
                onChange={(e) => setDeptForm({ ...deptForm, location: e.target.value })}
                placeholder="e.g., Bengaluru HQ"
              />
            </div>
            <div className="flex items-end">
              <Button type="submit" disabled={loading}>Add Department</Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Department -> Sub Department Hierarchy</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading && rows.length === 0 && <CardSkeleton rows={5} />}
          {rows.map((row) => (
            <div key={row.id} className="border border-border rounded-md p-3 space-y-3 card-interactive">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-foreground">{row.name}</p>
                  <p className="text-sm text-muted-foreground">Location: {row.location || 'N/A'}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={row.is_active ? 'success' : 'secondary'}>
                    {row.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                  <Button size="sm" variant="outline" onClick={() => toggleDepartment(row)}>
                    {row.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                </div>
              </div>

              <div className="grid md:grid-cols-4 gap-2">
                <Input
                  className="md:col-span-3"
                  value={subForms[row.id] || ''}
                  onChange={(e) => setSubForms((prev) => ({ ...prev, [row.id]: e.target.value }))}
                  placeholder="Add sub department"
                />
                <Button size="sm" onClick={() => addSubDepartment(row.id)}>Add Sub Dept</Button>
              </div>

              <div className="space-y-2">
                {(row.sub_departments || []).map((sub) => (
                  <div key={sub.id} className="border border-border rounded p-2">
                    <div className="flex items-center gap-2">
                      <Badge variant={sub.is_active ? 'outline' : 'secondary'}>{sub.name}</Badge>
                      <Button size="sm" variant="ghost" onClick={() => toggleSubDepartment(sub)}>
                        {sub.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                    </div>

                    <div className="grid md:grid-cols-4 gap-2 mt-2">
                      <Input
                        className="md:col-span-3"
                        value={designationForms[String(sub.id)] || ''}
                        onChange={(e) => setDesignationForms((prev) => ({ ...prev, [String(sub.id)]: e.target.value }))}
                        placeholder="Add designation"
                      />
                      <Button size="sm" onClick={() => addDesignation(row.name, sub.name, sub.id)}>Add Designation</Button>
                    </div>

                    <div className="flex flex-wrap gap-2 mt-2">
                      {(sub.designations || []).map((designation) => (
                        <div key={designation.id} className="border border-border rounded px-2 py-1 flex items-center gap-2">
                          <Badge variant={designation.is_active ? 'outline' : 'secondary'}>{designation.name}</Badge>
                          <Button size="sm" variant="ghost" onClick={() => toggleDesignation(designation)}>
                            {designation.is_active ? 'Deactivate' : 'Activate'}
                          </Button>
                        </div>
                      ))}
                      {(sub.designations || []).length === 0 && (
                        <p className="text-sm text-muted-foreground">No designations yet.</p>
                      )}
                    </div>
                  </div>
                ))}
                {(row.sub_departments || []).length === 0 && (
                  <p className="text-sm text-muted-foreground">No sub departments yet.</p>
                )}
              </div>
            </div>
          ))}
          {!loading && rows.length === 0 && (
            <EmptyState
              icon={Building2}
              title="No departments available"
              description="Create your first department above to start building the hierarchy."
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DepartmentMasterScreen;
